import {
  Table, Users, Upload, Download, SignOut, Sun, Moon, Key, UserCircle, Shield,
  ArrowsClockwise, Plus, List, CaretLeft, GearSix, FileArrowDown, UsersThree,
  ClockCounterClockwise, Sliders, Trash, Envelope,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Separator } from "../ui/separator";

export function Sidebar({
  user, isAdmin, theme, toggleTheme, logout,
  sidebarCollapsed, setSidebarCollapsed,
  appSettings, editingTitle, setEditingTitle, saveInlineTitle,
  loadingAction, openWithSpinner,
  setShowUploadDialog, setShowPasswordDialog, setShowOwnPasswordDialog,
  setShowUserManagement, setShowColumnConfig, setShowSettingsDialog,
  setShowTrashDialog, handleClearTable,
  setShowSmtpConfig,
}) {
  const navigate = useNavigate();

  const NavBtn = ({ testId, onClick, icon: Icon, label, isActive, isDanger, spinning }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded text-xs sm:text-sm transition-colors ${sidebarCollapsed ? 'justify-center' : 'justify-center sm:justify-start'} ${
        isActive ? 'bg-primary text-primary-foreground font-medium' :
        isDanger ? 'text-destructive hover:bg-destructive/10' :
        'text-foreground hover:bg-secondary'
      }`}
      title={label}
    >
      {spinning ? (
        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full flex-shrink-0" />
      ) : (
        <Icon className="h-4 w-4 flex-shrink-0" weight={isActive ? "bold" : undefined} />
      )}
      {!sidebarCollapsed && <span className="hidden sm:inline">{label}</span>}
    </button>
  );

  return (
    <aside className={`sidebar flex flex-col h-screen flex-shrink-0 transition-all duration-300 ${sidebarCollapsed ? 'w-12 sm:w-16' : 'w-14 sm:w-56'}`}>
      <div className="p-2 sm:p-4 flex items-center justify-between">
        {!sidebarCollapsed && (
          isAdmin && editingTitle === 'app' ? (
            <input
              type="text"
              defaultValue={appSettings.app_title}
              autoFocus
              className="hidden sm:block text-xl font-black uppercase tracking-tight bg-transparent border-b border-primary outline-none w-full"
              onBlur={(e) => saveInlineTitle('app_title', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveInlineTitle('app_title', e.target.value);
                if (e.key === 'Escape') setEditingTitle(null);
              }}
            />
          ) : (
            <h1
              className={`hidden sm:block text-xl font-black uppercase tracking-tight ${isAdmin ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
              onClick={() => isAdmin && setEditingTitle('app')}
              title={isAdmin ? "Klicken zum Bearbeiten" : undefined}
            >
              {appSettings.app_title}
            </h1>
          )
        )}
        <button
          data-testid="sidebar-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1.5 sm:p-2 rounded hover:bg-secondary transition-colors mx-auto sm:mx-0"
          title={sidebarCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
        >
          <CaretLeft className={`h-4 w-4 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <Separator />

      <div className="p-1 sm:p-2 space-y-3">
        <div className={`flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 bg-secondary rounded ${sidebarCollapsed ? 'justify-center' : 'justify-center sm:justify-start'}`}>
          {isAdmin ? (
            <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" weight="fill" />
          ) : (
            <UserCircle className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" weight="fill" />
          )}
          {!sidebarCollapsed && (
            <div className="hidden sm:block overflow-hidden">
              <p className="font-medium text-sm truncate">{user?.username}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                {isAdmin ? "Administrator" : "Benutzer"}
              </p>
            </div>
          )}
        </div>
      </div>

      <Separator />

      <nav className="flex-1 p-1 sm:p-2 space-y-1">
        <NavBtn testId="nav-dashboard" icon={Table} label="Dashboard" isActive />
        {isAdmin && (
          <>
            <NavBtn testId="nav-logs-versions" onClick={() => navigate("/admin")} icon={ClockCounterClockwise} label="Logs & Versionen" />
            <NavBtn testId="nav-upload" onClick={() => openWithSpinner('open-upload', setShowUploadDialog)} icon={Upload} label="CSV hochladen" spinning={loadingAction === 'open-upload'} />
            <NavBtn testId="nav-users" onClick={() => openWithSpinner('open-users', setShowUserManagement)} icon={UsersThree} label="Benutzer" spinning={loadingAction === 'open-users'} />
            <NavBtn testId="nav-columns" onClick={() => openWithSpinner('open-columns', setShowColumnConfig)} icon={GearSix} label="Spalten" spinning={loadingAction === 'open-columns'} />
            <NavBtn testId="nav-settings" onClick={() => openWithSpinner('open-settings', setShowSettingsDialog)} icon={Sliders} label="Einstellungen" spinning={loadingAction === 'open-settings'} />
            <NavBtn testId="nav-trash" onClick={() => openWithSpinner('open-trash', setShowTrashDialog)} icon={Trash} label="Papierkorb" spinning={loadingAction === 'open-trash'} />
            <NavBtn testId="nav-smtp" onClick={() => openWithSpinner('open-smtp', setShowSmtpConfig)} icon={Envelope} label="E-Mail (SMTP)" spinning={loadingAction === 'open-smtp'} />
            <NavBtn testId="nav-clear-table" onClick={handleClearTable} icon={Trash} label="Tabelle löschen" isDanger />
          </>
        )}
        <NavBtn testId="nav-own-password" onClick={() => setShowOwnPasswordDialog(true)} icon={UserCircle} label="Mein Profil" />
      </nav>

      <Separator />

      <div className="p-1 sm:p-2 space-y-1">
        <NavBtn
          testId="theme-toggle"
          onClick={toggleTheme}
          icon={theme === "dark" ? Sun : Moon}
          label={theme === "dark" ? "Hell" : "Dunkel"}
        />
        <NavBtn testId="logout-button" onClick={logout} icon={SignOut} label="Abmelden" isDanger />
      </div>
    </aside>
  );
}
